import React from 'react';
import { ExternalLink } from 'lucide-react';
import LegalLinks from '@/components/legal/LegalLinks';

/**
 * The Pam Golding page footer, mirroring the pamgolding.co.za layout:
 * gates mark on the left, centred wordmark + copyright, 50-years mark on the
 * right, collapsing to a stack on narrow screens.
 *
 * Deliberately shared between the public landing page and the Brand Kit
 * preview — they used to be two hand-rolled blocks that drifted, so the
 * "preview" showed something the visitor never actually saw. One component
 * means the preview can't lie.
 *
 * Every value is driven by brand settings; nothing here is hardcoded except
 * the two brand marks and the legal links, which are fixed. The legal links
 * show even when the brand kit has no footer, so no public page goes out
 * without them.
 */
export default function BrandFooter({ brand, className = '', onLead }) {
  const footer = brand?.footer;
  if (!footer) {
    return (
      <footer
        className={`bg-white px-6 py-6 text-gray-600 ${className}`}
        data-testid="brand-footer-legal-only"
      >
        <LegalLinks />
      </footer>
    );
  }

  const bg = footer.background_color || brand?.colors?.footer_bg || '#144733';
  const fg = footer.text_color || brand?.colors?.footer_text || '#ffffff';
  const linkColor = footer.link_color || '#ffffff';
  const social = footer.show_social_links ? brand?.social_links || {} : {};

  const contactLinks = [
    footer.phone && { key: 'phone', href: `tel:${footer.phone}`, label: footer.phone },
    footer.email && { key: 'email', href: `mailto:${footer.email}`, label: footer.email },
    footer.website_url && {
      key: 'web',
      href: footer.website_url,
      label: 'Website',
      external: true,
    },
  ].filter(Boolean);

  const socialLinks = [
    social.facebook && { key: 'facebook', href: social.facebook, path: ICONS.facebook },
    social.instagram && { key: 'instagram', href: social.instagram, path: ICONS.instagram },
    social.twitter && { key: 'twitter', href: social.twitter, path: ICONS.twitter },
    social.linkedin && { key: 'linkedin', href: social.linkedin, path: ICONS.linkedin },
    social.youtube && { key: 'youtube', href: social.youtube, path: ICONS.youtube },
    social.tiktok && { key: 'tiktok', href: social.tiktok, path: ICONS.tiktok },
    social.whatsapp && {
      key: 'whatsapp',
      // Accept either a full wa.me URL or a bare number.
      href: social.whatsapp.startsWith('http')
        ? social.whatsapp
        : `https://wa.me/${social.whatsapp.replace(/\D/g, '')}`,
      path: ICONS.whatsapp,
    },
  ].filter(Boolean);

  return (
    <footer
      className={`px-6 py-10 ${className}`}
      style={{ backgroundColor: bg, color: fg }}
      data-testid="brand-footer"
    >
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 sm:flex-row sm:justify-between">
        {/* Gates — left */}
        <img
          src="/pgp-gates-white.svg"
          alt="Pam Golding Properties"
          className="h-14 w-auto shrink-0 opacity-95"
          data-testid="footer-gates"
        />

        {/* Centre column */}
        <div className="order-last w-full text-center sm:order-none sm:flex-1 sm:px-6">
          {footer.tagline && (
            <p
              className="text-xl font-light uppercase tracking-[0.18em] sm:text-2xl"
              data-testid="footer-tagline"
            >
              {footer.tagline}
            </p>
          )}
          {footer.company_name && !footer.tagline && (
            <p className="text-xl font-light uppercase tracking-[0.18em]">
              {footer.company_name}
            </p>
          )}

          {footer.address && (
            <p className="mt-3 text-sm opacity-75">{footer.address}</p>
          )}

          {contactLinks.length > 0 && (
            <div className="mt-3 flex flex-wrap justify-center gap-4">
              {contactLinks.map((l) => (
                <a
                  key={l.key}
                  href={l.href}
                  {...(l.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  {...(!l.external && (l.key === 'phone' || l.key === 'email')
                    ? { onClick: () => onLead?.(`footer:${l.key}`) }
                    : {})}
                  className="inline-flex items-center gap-1 text-sm opacity-80 transition-opacity hover:opacity-100"
                  style={{ color: linkColor }}
                >
                  {l.external && <ExternalLink className="h-3 w-3" />}
                  {l.label}
                </a>
              ))}
            </div>
          )}

          {socialLinks.length > 0 && (
            <div className="mt-4 flex justify-center gap-4" data-testid="footer-social">
              {socialLinks.map((s) => (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.key}
                  className="opacity-75 transition-opacity hover:opacity-100"
                  style={{ color: linkColor }}
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d={s.path} />
                  </svg>
                </a>
              ))}
            </div>
          )}

          {footer.copyright_text && (
            <p className="mt-5 text-xs opacity-70" data-testid="footer-copyright">
              {footer.copyright_text}
            </p>
          )}
          {footer.show_powered_by && (
            <p className="mt-1 text-xs opacity-50">Powered by Pam Golding Properties</p>
          )}

          <LegalLinks color={fg} className="mt-4" />
        </div>

        {/* 50 years — right */}
        <img
          src="/pgp-50-years-white.svg"
          alt="Celebrating 50 years"
          className="h-14 w-auto shrink-0 opacity-95"
          data-testid="footer-50years"
        />
      </div>
    </footer>
  );
}

// Single-path brand glyphs, kept out of the markup above for readability.
const ICONS = {
  facebook:
    'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  instagram:
    'M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z',
  twitter:
    'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  linkedin:
    'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  youtube:
    'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
  tiktok:
    'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
  whatsapp:
    'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z',
};
